import { Check } from 'lucide-react';
import { TERMS } from '@pageboss/shared';
import { buttonVariants } from '@/components/ui/button';
import { useCanManageGbp } from '@/hooks/use-can-manage-gbp';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { gbpConnectHref } from '@/lib/api-paths';
import { cn } from '@/lib/utils';

// design brief §5.1: one sage primary button + an honest 3-line list of what
// connecting gets you. No fake preview, no sample data, no "here's what it
// will look like" screen.
const BENEFITS = [
  "See your real Google reviews, right in your dashboard",
  "Catch it fast when your name, address, or phone stop matching across Google and your site",
  "Track how customers find and reach you from your Google listing",
];

export function NotConnectedPanel({
  siteId,
  returnTo,
}: {
  siteId: string;
  returnTo?: 'setup';
}) {
  // Connecting is owner-only (gbp:manage). Anyone else sees what connecting
  // would give them, and who can do it, instead of a button the server
  // would refuse.
  const businessId = useBusinessId();
  const canManage = useCanManageGbp();
  return (
    <div className="rounded-xl border border-border-subtle bg-card p-8">
      <div className="max-w-prose">
        <h2 className="text-xl font-semibold tracking-tight">
          {canManage
            ? `Connect your ${TERMS.googleProfile}`
            : `Your ${TERMS.googleProfile} isn't connected yet`}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-text-secondary">
          {canManage
            ? "Link your Google account so this screen can show what's actually happening with your listing."
            : `Only the business owner can connect the ${TERMS.googleProfile}. Once they do, this screen shows what's actually happening with your listing.`}
        </p>
      </div>
      {canManage && businessId ? (
        <a
          href={gbpConnectHref(businessId, siteId, returnTo === 'setup' ? { from: 'setup' } : {})}
          className={cn(buttonVariants(), 'mt-5')}
        >
          Connect {TERMS.googleProfile}
        </a>
      ) : null}
      <ul className="mt-6 flex flex-col gap-2.5">
        {BENEFITS.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-text-secondary">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
